import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AlertController, Platform } from '@ionic/angular';
import { Subscription, firstValueFrom, of, timer } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/** Response from GET users-api/v1/application/mobile (no auth). */
export interface MobileApplicationVersions {
  androidVersion: string;
  iosVersion: string;
  lbAndroidVersion: string;
  lbIosVersion: string;
}

/** Test seams — Capacitor plugins are not reliably spy-able. */
export const capacitorAppInfo = {
  getInfo: () => App.getInfo(),
};

export const capacitorBrowser = {
  open: (options: Parameters<typeof Browser.open>[0]) => Browser.open(options),
};

/** Converts dotted iOS version (e.g. 8.00.01) to a comparable integer (80001). */
export function iosVersionToNumber(version: string): number {
  const digits = version.replace(/\./g, '');
  const n = Number(digits);
  return Number.isFinite(n) ? n : Number.NaN;
}

@Injectable({ providedIn: 'root' })
export class ForceUpdateService {
  private readonly http = inject(HttpClient);
  private readonly alert = inject(AlertController);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private dialogActive = false;
  private backSub: Subscription | undefined;

  private get mobileVersionsUrl(): string {
    return `${environment.endpointBasePaths.userApiUrl}application/mobile`;
  }

  /** Native only. Fail-open on network/parse errors. */
  async checkAndPrompt(): Promise<void> {
    if (!Capacitor.isNativePlatform() || this.dialogActive) {
      return;
    }

    await this.waitForUiReady();

    try {
      const versions = await firstValueFrom(this.http.get<MobileApplicationVersions>(this.mobileVersionsUrl));
      const needsUpdate = await this.platformNeedsUpdate(versions);
      if (!needsUpdate) {
        return;
      }
      await this.showForceUpdateDialog(Capacitor.getPlatform());
    } catch {
      // Fail-open: never block the app if version check fails.
    }
  }

  /** Wait until Ionic + initial redirect (/ → /inicio) settle so the alert is not dismissed by navigation. */
  private async waitForUiReady(): Promise<void> {
    await this.platform.ready();
    await firstValueFrom(
      this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        take(1),
        timeout({ first: 2500, with: () => of(null) })
      )
    );
    await firstValueFrom(timer(400));
  }

  private async platformNeedsUpdate(versions: MobileApplicationVersions): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') {
      return this.androidNeedsUpdate(versions.lbAndroidVersion);
    }
    if (platform === 'ios') {
      return this.iosNeedsUpdate(versions.lbIosVersion);
    }
    return false;
  }

  private async androidNeedsUpdate(minVersion: string): Promise<boolean> {
    const info = await capacitorAppInfo.getInfo();
    console.log('info', info);
    const installed = Number(info.build);
    const minimum = Number(minVersion);
    console.log('installed', installed, 'minimum', minimum);
    console.log(!Number.isFinite(installed) || !Number.isFinite(minimum));
    if (!Number.isFinite(installed) || !Number.isFinite(minimum)) {
      return false;
    }
    return installed < minimum;
  }

  private async iosNeedsUpdate(minVersion: string): Promise<boolean> {
    const info = await capacitorAppInfo.getInfo();
    console.log('info', info);
    const installed = iosVersionToNumber(info.version);
    const minimum = iosVersionToNumber(minVersion);
    console.log('installed', installed, 'minimum', minimum);
    if (!Number.isFinite(installed) || !Number.isFinite(minimum)) {
      return false;
    }
    return installed < minimum;
  }

  /**
   * Mandatory update dialog. If Ionic dismisses it (e.g. route change / back), it is shown again.
   */
  private async showForceUpdateDialog(platform: string): Promise<void> {
    this.dialogActive = true;
    await this.presentForceUpdateAlert(platform);
  }

  private async presentForceUpdateAlert(platform: string): Promise<void> {
    const alert = await this.alert.create({
      header: 'Nueva versión disponible',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Actualizar',
          role: 'confirm',
          handler: () => {
            const url = this.storeUrlFor(platform);
            if (url) {
              void capacitorBrowser.open({ url });
            }
            return false;
          },
        },
      ],
    });

    this.backSub?.unsubscribe();
    this.backSub = this.platform.backButton.subscribeWithPriority(9999, () => undefined);

    await alert.present();

    void alert.onDidDismiss().then(async () => {
      this.backSub?.unsubscribe();
      this.backSub = undefined;
      if (!this.dialogActive) {
        return;
      }
      await firstValueFrom(timer(150));
      if (this.dialogActive) {
        await this.presentForceUpdateAlert(platform);
      }
    });
  }

  /** CMS: CANALES_VENTA_APPS_IOS on iOS, CANALES_VENTA_APPS_ANDROID on Android. */
  private storeUrlFor(platform: string): string {
    const apps = environment.canalesVenta.apps;
    return platform === 'ios' ? apps.ios : apps.android;
  }
}
